/**
 * Time-of-day helpers and the rotating literary quote used by the Home page.
 * (The generated background-image rotation that used to live here was retired
 * with the clean "paper" redesign.)
 */

export type TimeOfDay = 'morning' | 'afternoon' | 'evening' | 'night';

// ─── Time of Day ──────────────────────────────────────────────────────────────

export function getTimeOfDay(hour?: number): TimeOfDay {
  const h = hour ?? new Date().getHours();
  if (h >= 6 && h < 12) return 'morning';
  if (h >= 12 && h < 17) return 'afternoon';
  if (h >= 17 && h < 20) return 'evening';
  return 'night';
}

// ─── Season ───────────────────────────────────────────────────────────────────

export function getSeason(month?: number): string {
  const m = month ?? new Date().getMonth() + 1;
  if (m >= 3 && m <= 5) return 'spring';
  if (m >= 6 && m <= 8) return 'summer';
  if (m >= 9 && m <= 11) return 'autumn';
  return 'winter';
}

// ─── Daily Seed ───────────────────────────────────────────────────────────────

/** Simple hash for deterministic daily rotation */
function dailySeed(): number {
  const today = new Date();
  const dateStr = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}`;
  let hash = 0;
  for (let i = 0; i < dateStr.length; i++) {
    const char = dateStr.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash |= 0;
  }
  return Math.abs(hash);
}

// ─── Literary Quotes ──────────────────────────────────────────────────────────

const LITERARY_QUOTES = [
  { text: 'There is no charm equal to tenderness of heart.', author: 'Jane Austen' },
  {
    text: 'Tell me, what is it you plan to do with your one wild and precious life?',
    author: 'Mary Oliver',
  },
  { text: 'We loved with a love that was more than love.', author: 'Edgar Allan Poe' },
  {
    text: 'I am not afraid of storms, for I am learning how to sail my ship.',
    author: 'Louisa May Alcott',
  },
  { text: 'The soul that sees beauty may sometimes walk alone.', author: 'Goethe' },
  {
    text: 'She was beautiful, but not like those girls in the magazines. She was beautiful, for the way she thought.',
    author: 'F. Scott Fitzgerald',
  },
  {
    text: 'I have loved the stars too fondly to be fearful of the night.',
    author: 'Sarah Williams',
  },
  {
    text: 'In the middle of winter, I at last discovered that there was in me an invincible summer.',
    author: 'Albert Camus',
  },
  { text: 'Whatever our souls are made of, his and mine are the same.', author: 'Emily Brontë' },
  { text: 'The wound is the place where the Light enters you.', author: 'Rumi' },
  { text: 'I dwell in Possibility.', author: 'Emily Dickinson' },
  {
    text: 'And the day came when the risk to remain tight in a bud was more painful than the risk it took to blossom.',
    author: 'Anaïs Nin',
  },
  {
    text: 'She was a wild one; always stomping on eggshells that everyone else tiptoed on.',
    author: 'Kaitlin Foster',
  },
  { text: 'In a gentle way, you can shake the world.', author: 'Mahatma Gandhi' },
  {
    text: 'The most beautiful things in the world cannot be seen or even touched. They must be felt with the heart.',
    author: 'Helen Keller',
  },
  { text: 'Light tomorrow with today.', author: 'Elizabeth Barrett Browning' },
  { text: 'To love and be loved is to feel the sun from both sides.', author: 'David Viscott' },
  {
    text: 'One must still have chaos in oneself to be able to give birth to a dancing star.',
    author: 'Nietzsche',
  },
  {
    text: 'Perhaps all the dragons in our lives are princesses who are only waiting to see us act, just once, with beauty and courage.',
    author: 'Rilke',
  },
  {
    text: 'You yourself, as much as anybody in the entire universe, deserve your love and affection.',
    author: 'Buddha',
  },
];

/**
 * Returns a curated literary quote that rotates daily using a date-based seed.
 */
export function getDailyQuote(): { text: string; author: string } {
  const seed = dailySeed();
  return LITERARY_QUOTES[seed % LITERARY_QUOTES.length];
}
